import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Sparkles, BookOpen, Check, Heart, UserCheck, HeartHandshake, HeartCrack, Flame, Phone, ArrowRight, Star } from 'lucide-react';
import { services } from '../data/mock';
import { useAuth } from '../context/AuthContext';

const iconMap = {
  brain: Brain,
  sparkles: Sparkles,
  'book-open': BookOpen,
  heart: Heart,
  'user-check': UserCheck,
  'heart-handshake': HeartHandshake,
  'heart-crack': HeartCrack,
  flame: Flame
};

// Map landing-page mock ids to backend service_ids
const serviceIdMap = {
  1: 'nlp',
  2: 'reiki',
  3: 'akashic',
  4: 'women-relationship',
  5: 'men-relationship',
  6: 'pre-marital',
  7: 'relationship-pain',
  8: 'live-in',
};

// Visible badges that match backend seed
const badgeMap = {
  1: 'Most Popular',
  6: 'Recommended',
};

const Services = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const goBook = (mockId) => {
    const serviceId = serviceIdMap[mockId];
    if (!serviceId) return;
    if (user) {
      navigate(`/book/${serviceId}`);
    } else {
      navigate('/signup', { state: { from: `/book/${serviceId}` } });
    }
  };

  const goDiscovery = () => {
    if (user) {
      navigate('/book/discovery');
    } else {
      navigate('/signup', { state: { from: '/book/discovery' } });
    }
  };

  return (
    <section id="services" className="services-section">
      <div className="container">
        <div className="section-header">
          <h2 className="heading-1">Transformational Services</h2>
          <p className="body-large section-subtitle">
            Choose the path that resonates with your soul&apos;s calling
          </p>
        </div>

        <div className="services-grid">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <div 
                key={service.id} 
                data-testid={`service-card-${service.id}`}
                className={`service-card accent-${service.accentColor}`}
              >
                {badgeMap[service.id] && (
                  <span
                    className={`service-card-badge ${badgeMap[service.id] === 'Most Popular' ? 'badge-popular' : 'badge-recommended'}`}
                    data-testid={`service-badge-${service.id}`}
                  >
                    <Star size={12} fill="currentColor" /> {badgeMap[service.id]}
                  </span>
                )}
                <div className="service-icon">
                  <IconComponent size={32} />
                </div>
                
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                
                <ul className="service-features">
                  {service.features.map((feature) => (
                    <li key={feature} className="feature-item">
                      <Check size={16} className="check-icon" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="service-footer">
                  <div className="service-meta">
                    <span className="service-duration">{service.duration}</span>
                    {service.price && (
                      <span className="service-price" data-testid={`service-price-${service.id}`}>
                        ₹{service.price.toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                  <button onClick={() => goBook(service.id)} data-testid={`book-now-btn-${service.id}`} className="btn-primary btn-small">
                    Book Now
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="discovery-cta" data-testid="discovery-call-cta">
          <div className="discovery-cta-icon">
            <Phone size={28} />
          </div>
          <div className="discovery-cta-content">
            <h3 className="discovery-cta-title">In doubt which service is right for you?</h3>
            <p className="discovery-cta-text">
              Book a <strong>Free 15-min Discovery Call</strong> — we&apos;ll listen to where you are right now and gently guide you to the modality that fits your journey best. No pressure, no obligation.
            </p>
          </div>
          <button
            onClick={goDiscovery}
            data-testid="discovery-call-btn"
            className="btn-primary btn-large"
          >
            Book Free Call
            <ArrowRight size={18} style={{ marginLeft: '0.5rem' }} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Services;
